import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CheckCircle2, XCircle, AlertTriangle, Building2, KeyRound, RefreshCw, Landmark, Tags, ScrollText, FileText } from 'lucide-react';
import { FocusLastRequestModal } from './FocusLastRequestModal';
import { useProductCategories } from '../lib/categories';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMITENTE_FIELDS = [
  { name: 'legal_name', label: 'Razão Social *', ph: 'MARGE HORTIFRUTI LTDA' },
  { name: 'cnpj', label: 'CNPJ *', ph: '00.000.000/0001-00' },
  { name: 'ie', label: 'Inscrição Estadual (IE) *', ph: 'somente números' },
  { name: 'address', label: 'Logradouro *', ph: 'Rua / Avenida' },
  { name: 'address_number', label: 'Número *', ph: '123' },
  { name: 'neighborhood', label: 'Bairro *', ph: 'Centro' },
  { name: 'zip_code', label: 'CEP *', ph: '79000-000' },
  { name: 'city', label: 'Município *', ph: 'Campo Grande' },
  { name: 'state', label: 'UF *', ph: 'MS' },
  { name: 'ibge_code', label: 'Código IBGE do município *', ph: '5002704 (Campo Grande)' },
  { name: 'address_complement', label: 'Complemento', ph: 'Sala 1, Loja B (opcional)' },
  { name: 'phone', label: 'Telefone', ph: '(67) 99999-9999 (opcional)' },
];

export const FiscalSettingsPanel = ({ settings, onChange, onSaved }) => {
  const set = (k, v) => onChange({ ...settings, [k]: v });
  const categories = useProductCategories();
  const [readiness, setReadiness] = useState(null);
  const [syncMsg, setSyncMsg] = useState({ type: '', text: '' });
  const [busy, setBusy] = useState(false);
  const [showFocusLog, setShowFocusLog] = useState(false);
  const amb = settings.nfce_ambiente || 'homologacao';

  const loadReadiness = () => axios.get(`${API}/nfce/readiness`, { withCredentials: true }).then((r) => setReadiness(r.data)).catch(() => {});
  useEffect(() => { loadReadiness(); }, [settings.focus_company_synced_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const syncCompany = async () => {
    setBusy(true); setSyncMsg({ type: '', text: '' });
    try {
      await onSaved?.();
      const r = await axios.post(`${API}/nfce/empresa/sync`, {}, { withCredentials: true });
      setSyncMsg({ type: 'ok', text: `Empresa ${r.data.action} na Focus NFe (${r.data.focus?.nome || r.data.focus?.cnpj})${r.data.tokens_saved ? ' — tokens da empresa salvos automaticamente.' : '.'}` });
      loadReadiness();
      const fresh = await axios.get(`${API}/settings/company`, { withCredentials: true });
      onChange(fresh.data);
    } catch (e) {
      const st = e.response?.status;
      const d = e.response?.data?.detail;
      const text = d ? `${st ? `[HTTP ${st}] ` : ''}${typeof d === 'string' ? d : JSON.stringify(d)}` : `Sem resposta do servidor (${e.code || e.message}) — verifique a conexão do backend com a internet`;
      console.error('[Focus NFe] sync empresa falhou:', st, e.response?.data || e.message);
      setSyncMsg({ type: 'err', text });
    } finally { setBusy(false); }
  };

  const fd = settings.fiscal_defaults || {};
  const setDefault = (cat, k, v) => set('fiscal_defaults', { ...fd, [cat]: { ...(fd[cat] || {}), [k]: v } });

  return (
    <div className="space-y-6" data-testid="fiscal-settings">
      {readiness && (
        <div className={`rounded-md border-2 p-4 ${readiness.ready ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-300'}`} data-testid="fiscal-readiness">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <p className="font-bold flex items-center gap-2">
              {readiness.ready ? <CheckCircle2 size={18} className="text-emerald-600" /> : <AlertTriangle size={18} className="text-amber-600" />}
              {readiness.ready ? 'Pronto para emitir NFC-e' : 'Faltam itens para emitir NFC-e com validade fiscal'}
              <span className="text-xs font-normal px-2 py-0.5 rounded bg-white border">{readiness.mode === 'mock' ? 'MODO RASCUNHO (sem transmissão)' : `Focus NFe · ${readiness.ambiente.toUpperCase()}`}</span>
            </p>
            <Button size="sm" variant="outline" onClick={loadReadiness} data-testid="fiscal-readiness-refresh"><RefreshCw size={14} className="mr-1" /> Reverificar</Button>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm">
            {readiness.checks.map((c) => (
              <li key={c.key} className="flex items-center gap-2" data-testid={`fiscal-check-${c.key}`}>
                {c.ok ? <CheckCircle2 size={14} className="text-emerald-600 shrink-0" /> : (c.warn ? <AlertTriangle size={14} className="text-amber-600 shrink-0" /> : <XCircle size={14} className="text-red-600 shrink-0" />)}
                <span className={c.ok ? 'text-slate-700' : 'font-medium text-slate-900'}>{c.label}{!c.ok && c.detail ? ` — ${c.detail}` : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md p-4">
        <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><Building2 size={16} /> Emitente (dados fiscais)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {EMITENTE_FIELDS.map((f) => (
            <div key={f.name} className="space-y-1">
              <Label className="text-xs">{f.label}</Label>
              <Input value={settings[f.name] || ''} onChange={(e) => set(f.name, e.target.value)} placeholder={f.ph} data-testid={`fiscal-${f.name}`} />
            </div>
          ))}
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1"><Landmark size={12} /> Regime tributário *</Label>
            <select value={settings.tax_regime || ''} onChange={(e) => set('tax_regime', e.target.value)} className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm" data-testid="fiscal-tax-regime">
              <option value="">Selecione…</option>
              <option value="simples">Simples Nacional (CRT 1 — CSOSN)</option>
              <option value="simples_excesso">Simples Nacional — excesso de sublimite (CRT 2)</option>
              <option value="presumido">Lucro Presumido (CRT 3 — CST)</option>
              <option value="real">Lucro Real (CRT 3 — CST)</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4">
        <h3 className="font-bold text-slate-900 mb-1 flex items-center gap-2"><KeyRound size={16} /> Focus NFe (transmissão para a SEFAZ-MS)</h3>
        <p className="text-xs text-slate-500 mb-3">Crie a conta em focusnfe.com.br, cadastre a empresa e cole aqui o token de cada ambiente. O token nunca é exibido ao operador nem enviado ao navegador do PDV.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs font-bold">Token da CONTA (master) *</Label>
            <Input type="password" value={settings.focus_account_token || ''} onChange={(e) => set('focus_account_token', e.target.value)} placeholder="Painel Focus → Configurações → Tokens" autoComplete="off" data-testid="fiscal-account-token" />
            <p className="text-[10px] text-slate-500">Usado só para cadastrar/atualizar a empresa (API de empresas, sempre em produção). Ao sincronizar, os tokens da empresa abaixo são preenchidos automaticamente.</p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Token — Homologação (testes)</Label>
            <Input type="password" value={settings.focus_token_homologacao || ''} onChange={(e) => set('focus_token_homologacao', e.target.value)} placeholder="token de homologação" autoComplete="off" data-testid="fiscal-token-homologacao" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Token — Produção (validade fiscal)</Label>
            <Input type="password" value={settings.focus_token_producao || ''} onChange={(e) => set('focus_token_producao', e.target.value)} placeholder="token de produção" autoComplete="off" data-testid="fiscal-token-producao" />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button type="button" onClick={syncCompany} disabled={busy || !settings.focus_account_token} className="bg-slate-900 hover:bg-black" data-testid="fiscal-sync-company">
            <RefreshCw size={16} className={`mr-2 ${busy ? 'animate-spin' : ''}`} /> Salvar e sincronizar empresa na Focus
          </Button>
          <span className="text-xs text-slate-500">Envia emitente, certificado A1 e CSC ({amb}) usando o token da conta.</span>
          <Button type="button" variant="outline" onClick={() => setShowFocusLog(true)} data-testid="fiscal-view-last-request">
            <ScrollText size={16} className="mr-2" /> Ver última requisição à Focus
          </Button>
        </div>
        {showFocusLog && <FocusLastRequestModal onClose={() => setShowFocusLog(false)} />}
        {syncMsg.text && <p className={`text-sm font-medium mt-2 break-words ${syncMsg.type === 'ok' ? 'text-emerald-700' : 'text-red-600'}`} data-testid="fiscal-sync-msg">{syncMsg.text}</p>}
        <div className="hidden">
        </div>
      </div>

      <div className="bg-white border-2 border-indigo-200 rounded-md p-4" data-testid="nfe-settings">
        <h3 className="font-bold text-slate-900 mb-1 flex items-center gap-2"><FileText size={16} /> NF-e (modelo 55) — série e numeração</h3>
        <p className="text-xs text-slate-500 mb-3">A NF-e usa o mesmo ambiente ({amb}), certificado e token da NFC-e, mas tem série e numeração próprias. <b>Se a empresa já emitiu NF-e em outro sistema, informe aqui o último número emitido + 1</b> para continuar a sequência e evitar rejeição por duplicidade (a numeração da NFC-e é automática e não precisa ser alterada).</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Série da NF-e</Label>
            <Input value={settings.nfe_serie || '1'} onChange={(e) => set('nfe_serie', e.target.value)} placeholder="1" data-testid="nfe-serie" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-bold">Próximo número da NF-e *</Label>
            <Input type="number" min={1} value={settings.nfe_next_number || 1} onChange={(e) => set('nfe_next_number', parseInt(e.target.value || '1', 10))} placeholder="ex.: 1251" data-testid="nfe-next-number" />
            <p className="text-[10px] text-slate-500">A próxima NF-e sairá com este número e a sequência continua automaticamente. Salve com "Salvar Fiscal".</p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4">
        <h3 className="font-bold text-slate-900 mb-1 flex items-center gap-2"><Tags size={16} /> Tributação padrão por categoria</h3>
        <p className="text-xs text-slate-500 mb-3">Usada quando o produto não tem NCM/CFOP/CSOSN próprios. CSOSN vale para Simples Nacional; CST para Presumido/Real. Confirme os códigos com o contador.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="fiscal-defaults-table">
            <thead className="bg-slate-50"><tr>
              <th className="px-2 py-2 text-left text-[10px] uppercase font-bold text-slate-500">Categoria</th>
              <th className="px-2 py-2 text-left text-[10px] uppercase font-bold text-slate-500">NCM</th>
              <th className="px-2 py-2 text-left text-[10px] uppercase font-bold text-slate-500">CFOP</th>
              <th className="px-2 py-2 text-left text-[10px] uppercase font-bold text-slate-500">CSOSN</th>
              <th className="px-2 py-2 text-left text-[10px] uppercase font-bold text-slate-500">CST</th>
              <th className="px-2 py-2 text-left text-[10px] uppercase font-bold text-slate-500">Origem</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {categories.map((c) => {
                const base = readiness?.fiscal_defaults?.[c.value] || readiness?.fiscal_defaults?.outros || {};
                const row = fd[c.value] || {};
                const cell = (k, w) => <Input value={row[k] ?? ''} placeholder={base[k] || ''} onChange={(e) => setDefault(c.value, k, e.target.value)} className={`h-8 text-xs ${w}`} data-testid={`fiscal-default-${c.value}-${k}`} />;
                return (
                  <tr key={c.value}>
                    <td className="px-2 py-1 font-medium">{c.label.split(' (')[0]}</td>
                    <td className="px-2 py-1">{cell('ncm', 'w-28')}</td>
                    <td className="px-2 py-1">{cell('cfop', 'w-20')}</td>
                    <td className="px-2 py-1">{cell('csosn', 'w-20')}</td>
                    <td className="px-2 py-1">{cell('cst', 'w-20')}</td>
                    <td className="px-2 py-1">{cell('origem', 'w-16')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
